import { DateTime } from "luxon";
import { z } from "zod";

export const CUSTOMER_WINDOW_PROTOCOL = 1 as const;
export const CUSTOMER_WINDOW_MAX_AGE_MS = 5_000;
export const CUSTOMER_WINDOW_REFRESH_MS = 2_000;
export const CUSTOMER_WINDOW_MAX_BYTES = 512 * 1024;
export const CustomerWindowTabSchema = z.enum(["all", "transferred"]);
export type CustomerWindowTab = z.infer<typeof CustomerWindowTabSchema>;

export const CustomerWindowAuthorizationSchema = z
  .object({
    authorizationId: z.string().uuid(),
    scopeKey: z.string().min(1).max(128),
    policyKey: z.string().min(1).max(128),
    leanRows: z.boolean(),
    emailDashboardFlagOffTenantIds: z.array(z.string().uuid()).max(250).optional(),
  })
  .strict();
export type CustomerWindowAuthorization = z.infer<typeof CustomerWindowAuthorizationSchema>;

// These are reconciled window versions, not durable change-log cursors. Reconnects resnapshot.
export const CustomerListWindowSnapshotSchema = z
  .object({
    version: z.string().min(1).max(128),
    sourceReadAt: z.string().datetime({ offset: true }),
    leaseExpiresAt: z.string().datetime({ offset: true }),
    nextCursor: z.string().nullable(),
    rows: z.array(z.record(z.unknown())).max(20),
  })
  .strict();
export type CustomerListWindowSnapshot = z.infer<typeof CustomerListWindowSnapshotSchema>;

export const CustomerWindowClientMessageSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("customer_window_subscribe"),
      protocolVersion: z.literal(CUSTOMER_WINDOW_PROTOCOL),
      subscriptionId: z.string().uuid(),
      authorizationId: z.string().uuid(),
      tab: CustomerWindowTabSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("customer_window_unsubscribe"),
      protocolVersion: z.literal(CUSTOMER_WINDOW_PROTOCOL),
      subscriptionId: z.string().uuid(),
    })
    .strict(),
]);
export type CustomerWindowClientMessage = z.infer<typeof CustomerWindowClientMessageSchema>;

const WindowEnvelopeSchema = z.object({
  protocolVersion: z.literal(CUSTOMER_WINDOW_PROTOCOL),
  subscriptionId: z.string().uuid(),
  authorizationId: z.string().uuid(),
  scopeKey: z.string().min(1).max(128),
  policyKey: z.string().min(1).max(128),
  tab: CustomerWindowTabSchema,
});

export const CustomerWindowServerMessageSchema = z.discriminatedUnion("type", [
  WindowEnvelopeSchema.extend({
    type: z.literal("customer_window_snapshot"),
    window: CustomerListWindowSnapshotSchema,
  }).strict(),
  WindowEnvelopeSchema.extend({
    type: z.literal("customer_window_delta"),
    previousVersion: z.string().min(1).max(128),
    window: CustomerListWindowSnapshotSchema,
  }).strict(),
  z
    .object({
      type: z.literal("customer_window_unavailable"),
      protocolVersion: z.literal(CUSTOMER_WINDOW_PROTOCOL),
      subscriptionId: z.string().uuid(),
      reason: z.enum(["disabled", "unhealthy", "authorization_changed", "backpressure"]),
    })
    .strict(),
]);
export type CustomerWindowServerMessage = z.infer<typeof CustomerWindowServerMessageSchema>;

export function isCustomerWindowFresh(
  window: CustomerListWindowSnapshot,
  now = DateTime.utc().toMillis()
): boolean {
  const sourceReadAt = DateTime.fromISO(window.sourceReadAt).toMillis();
  const expiresAt = DateTime.fromISO(window.leaseExpiresAt).toMillis();
  return (
    Number.isFinite(sourceReadAt) &&
    Number.isFinite(expiresAt) &&
    sourceReadAt <= now &&
    expiresAt > now &&
    expiresAt > sourceReadAt &&
    expiresAt <= sourceReadAt + CUSTOMER_WINDOW_MAX_AGE_MS
  );
}
